// ─── Dashboard View Model Composer ───────────────────────────────────────────
//
// Root pure function that takes raw dashboard inputs (the outputs of the
// existing engines: pillar scoring, phenotype grading, insights, exception
// detection, and the normalized data hook) and produces the full
// DashboardViewModel consumed by the UI layer.
//
// This is the single place where transformer functions are composed. The
// Dashboard page should do exactly one call: `buildDashboardVM(rawInputs)`
// and then pass the result to section components. No derivation in JSX.
// ──────────────────────────────────────────────────────────────────────────────

import type { DailyMetrics } from '../../types';
import type { PillarResult } from '../pillarScoring';
import type { PhenotypeProfile } from '../phenotype';
import type { Insight } from '../insights';
import type { Exception } from '../exceptionDetector';
import type { DashboardViewModel } from './types';
import { computeTrustIndicators } from './computeTrustIndicators';
import { computeSystemMap } from './computeSystemMap';
import { computePrimaryInsight } from './computePrimaryInsight';
import { computePatternInterpretation } from './computePatternInterpretation';

// ─── Input contract ──────────────────────────────────────────────────────────

/**
 * Everything the dashboard view model needs, expressed as already-computed
 * outputs from the existing engine layer. `useDashboardData()` + local
 * `useMemo`s in Dashboard.tsx are the source of these values; this composer
 * is just the aggregation seam.
 *
 * Keeping this shape explicit lets us test the composer in total isolation.
 */
export interface DashboardVMInput {
  metrics: DailyMetrics[];
  pillarResults: Record<string, PillarResult>;
  pillarDeltas?: Record<string, number>;
  sparklineData?: Record<string, number[]>;
  phenotypeProfile: PhenotypeProfile | null;
  insights: Insight[];
  exceptions: Exception[];
  /** Clock injection for deterministic tests. */
  now?: Date;
}

// ─── Composer ────────────────────────────────────────────────────────────────

/**
 * Produce the full dashboard view model. Pure and deterministic given its
 * inputs. Safe to call inside useMemo or a Jest test.
 */
export function buildDashboardVM(input: DashboardVMInput): DashboardViewModel {
  const {
    metrics,
    pillarResults,
    pillarDeltas,
    sparklineData,
    phenotypeProfile,
    insights,
    exceptions,
    now,
  } = input;

  const trust = computeTrustIndicators({ pillarResults, metrics, now });

  const systemOverview = computeSystemMap({
    pillarResults,
    pillarDeltas,
    sparklineData,
    trust,
  });

  const primaryInsight = computePrimaryInsight({
    exceptions,
    insights,
    pillarResults,
    pillarDeltas,
    trust,
  });

  const patternInterpretation = computePatternInterpretation({
    profile: phenotypeProfile,
  });

  return {
    primaryInsight,
    systemOverview,
    patternInterpretation,
  };
}
